/**
 * The ONE place a kitchen board column meets paio-ordering's TicketState
 * (ORDR-42).
 *
 * The vocabulary and the legal moves are transcribed from paio-ordering
 * `models/vocabularies.py` (TicketState, thirteen members) and
 * `services/fulfilment.py` (TICKET_TRANSITIONS) on origin/main 6a3b74a.
 * `ticketStates.test.ts` asserts every bump below is a legal move in that
 * table, so a mapping that names an illegal target fails here and not as a
 * 422 in a kitchen on a Friday.
 *
 * THE BOARD HAS THREE COLUMNS AND THE SERVER HAS THIRTEEN STATES:
 *
 * | column | TicketState                              | bump goes to     |
 * | ------ | ---------------------------------------- | ---------------- |
 * | queued | PENDING_ACK, UNACKED                     | ACCEPTED         |
 * | queued | ACCEPTED, ON_HOLD                        | IN_PREPARATION   |
 * | firing | IN_PREPARATION                           | READY            |
 * | ready  | READY                                    | COMPLETED        |
 * | ready  | DELIVERED                                | COMPLETED        |
 *
 * RECALL. The server has no "undo". The only legal backward move from a
 * working column is a hold, so a recall from firing or ready is a move to
 * ON_HOLD, which renders in queued and bumps back to IN_PREPARATION. A recall
 * from queued has no legal target and is refused.
 *
 * The courier leg (COURIER_ASSIGNED, PICKED_UP, EN_ROUTE) renders in ready and
 * has no bump: the courier moves it, not the cook.
 */

import type { KitchenPassState } from "../framework/types.js";

export const TICKET_STATES = [
  "PENDING_ACK",
  "ACCEPTED",
  "IN_PREPARATION",
  "READY",
  "ON_HOLD",
  "COMPLETED",
  "REJECTED",
  "CANCELLED",
  "UNACKED",
  "COURIER_ASSIGNED",
  "PICKED_UP",
  "EN_ROUTE",
  "DELIVERED",
] as const;

export type TicketState = (typeof TICKET_STATES)[number];

/** paio-ordering TICKET_TRANSITIONS, verbatim. */
export const TICKET_TRANSITIONS: Readonly<Record<TicketState, readonly TicketState[]>> = {
  PENDING_ACK: ["ACCEPTED", "REJECTED", "UNACKED", "CANCELLED"],
  UNACKED: ["ACCEPTED", "REJECTED", "CANCELLED"],
  ACCEPTED: ["IN_PREPARATION", "ON_HOLD", "CANCELLED"],
  IN_PREPARATION: ["READY", "ON_HOLD", "COURIER_ASSIGNED", "CANCELLED"],
  READY: ["COMPLETED", "COURIER_ASSIGNED", "ON_HOLD", "CANCELLED"],
  ON_HOLD: ["ACCEPTED", "IN_PREPARATION", "READY", "CANCELLED"],
  COURIER_ASSIGNED: ["PICKED_UP", "ON_HOLD", "CANCELLED"],
  PICKED_UP: ["EN_ROUTE", "CANCELLED"],
  EN_ROUTE: ["DELIVERED", "CANCELLED"],
  DELIVERED: ["COMPLETED"],
  COMPLETED: [],
  REJECTED: [],
  CANCELLED: [],
};

/** Terminal states never occupy a column; the feed does not send them. */
const COLUMN: Readonly<Record<TicketState, KitchenPassState | null>> = {
  PENDING_ACK: "queued",
  UNACKED: "queued",
  ACCEPTED: "queued",
  ON_HOLD: "queued",
  IN_PREPARATION: "firing",
  READY: "ready",
  COURIER_ASSIGNED: "ready",
  PICKED_UP: "ready",
  EN_ROUTE: "ready",
  DELIVERED: "ready",
  COMPLETED: null,
  REJECTED: null,
  CANCELLED: null,
};

const BUMP: Readonly<Partial<Record<TicketState, TicketState>>> = {
  PENDING_ACK: "ACCEPTED",
  UNACKED: "ACCEPTED",
  ACCEPTED: "IN_PREPARATION",
  ON_HOLD: "IN_PREPARATION",
  IN_PREPARATION: "READY",
  READY: "COMPLETED",
  DELIVERED: "COMPLETED",
};

const RECALL: Readonly<Partial<Record<TicketState, TicketState>>> = {
  IN_PREPARATION: "ON_HOLD",
  READY: "ON_HOLD",
};

export function isTicketState(value: unknown): value is TicketState {
  return typeof value === "string" && (TICKET_STATES as readonly string[]).includes(value);
}

/** The column a server state renders in, or `null` for a terminal state. */
export function columnFor(state: TicketState): KitchenPassState | null {
  return COLUMN[state];
}

/** Where a cook's bump sends a ticket, or `null` when the cook cannot move it. */
export function bumpTarget(state: TicketState): TicketState | null {
  return BUMP[state] ?? null;
}

/** Where a recall sends a ticket, or `null` when there is no legal way back. */
export function recallTarget(state: TicketState): TicketState | null {
  return RECALL[state] ?? null;
}
